import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import {
  Key as KeyIcon,
  Business as BusinessIcon,
  Apps as AppsIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';

interface StepMetaTokensProps {
  formData: any;
  onComplete: (data: any) => void;
  onNext: () => void;
  onBack?: () => void;
  isActive: boolean;
}

const metaTokensSchema = yup.object({
  api_token: yup.string().nullable(),
  phone_number_id: yup.string().nullable(),
  meta_business_id: yup.string().nullable(),
  meta_app_id: yup.string().nullable(),
});

export const StepMetaTokens: React.FC<StepMetaTokensProps> = ({
  formData,
  onComplete,
  onNext,
  onBack,
  isActive,
}) => {
  const formik = useFormik({
    initialValues: {
      api_token: formData?.api_token || '',
      phone_number_id: formData?.phone_number_id || '',
      meta_business_id: formData?.meta_business_id || '',
      meta_app_id: formData?.meta_app_id || '',
    },
    validationSchema: metaTokensSchema,
    onSubmit: (values) => {
      onComplete({
        api_token: values.api_token,
        phone_number_id: values.phone_number_id,
        meta_business_id: values.meta_business_id,
        meta_app_id: values.meta_app_id,
      });
    },
  });

  if (!isActive) return null;

  return (
    <Box sx={{ py: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Alert severity="info">
            Cette étape est facultative. Si vous n’avez pas encore les identifiants Meta, vous pouvez
            laisser ces champs vides et poursuivre.
          </Alert>
        </Grid>

        <Grid item xs={12} md={8}>
          <Box component="form" onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Token d'accès API WhatsApp"
                  name="api_token"
                  value={formik.values.api_token}
                  onChange={formik.handleChange}
                  placeholder="Optionnel"
                  error={formik.touched.api_token && Boolean(formik.errors.api_token)}
                  helperText={formik.touched.api_token && formik.errors.api_token}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <KeyIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ID du numéro de téléphone"
                  name="phone_number_id"
                  value={formik.values.phone_number_id}
                  onChange={formik.handleChange}
                  placeholder="Optionnel"
                  error={formik.touched.phone_number_id && Boolean(formik.errors.phone_number_id)}
                  helperText={formik.touched.phone_number_id && formik.errors.phone_number_id}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ID Meta Business"
                  name="meta_business_id"
                  value={formik.values.meta_business_id}
                  onChange={formik.handleChange}
                  placeholder="Optionnel"
                  error={formik.touched.meta_business_id && Boolean(formik.errors.meta_business_id)}
                  helperText={formik.touched.meta_business_id && formik.errors.meta_business_id}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ID de l'application Meta"
                  name="meta_app_id"
                  value={formik.values.meta_app_id}
                  onChange={formik.handleChange}
                  placeholder="Optionnel"
                  error={formik.touched.meta_app_id && Boolean(formik.errors.meta_app_id)}
                  helperText={formik.touched.meta_app_id && formik.errors.meta_app_id}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AppsIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  {onBack && <Button onClick={onBack}>Retour</Button>}
                  <Button type="submit" variant="contained" sx={{ minWidth: 120 }}>
                    Continuer
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Ce champ peut être complété plus tard
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Token API :</strong> facultatif pour le premier parcours.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Phone Number ID :</strong> récupérable dans Meta si le client a déjà finalisé
                sa configuration.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Business ID :</strong> utile uniquement si la connexion Meta est déjà en place.
              </Typography>
              <Typography variant="body2">
                DigitalCloud360 peut poursuivre l’activation avec les données disponibles.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};